import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Radar } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { SignalBacktestPanel } from "../components/SignalBacktestPanel";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const usd = (v) =>
  v == null ? "—" : v >= 1e9 ? `$${(v / 1e9).toFixed(1)}B` : `$${(v / 1e6).toFixed(1)}M`;
const pct = (v, dp = 1) => (v == null ? "—" : `${(v * 100).toFixed(dp)}%`);

const TIER_STYLE = {
  A: "bg-amber-500/15 text-amber-300 border-amber-500/50",
  B: "text-cyan-300 border-cyan-500/40",
  C: "text-muted-foreground border-border",
};
const TIER_LABEL = { A: "A · DEEP+LOSS ELITE", B: "B · DEEP+LOSS", C: "C · BASE" };

const SignalRow = ({ r }) => (
  <tr className="border-b border-border/40 hover:bg-muted/30"
      data-testid={`radar-row-${r.symbol}-${r.accepted_date}`}>
    <td className="py-2 px-2 font-bold">{r.symbol}</td>
    <td className="py-2 px-2">
      <Badge variant="outline" className={`text-[10px] ${TIER_STYLE[r.tier]}`}>
        {TIER_LABEL[r.tier]}
      </Badge>
    </td>
    <td className="py-2 px-2">
      {r.solo ? (
        <Badge variant="outline" className="text-[10px] text-emerald-300 border-emerald-500/40">SOLO</Badge>
      ) : (
        <Badge variant="outline" className="text-[10px] text-rose-300 border-rose-500/40">
          CONVERGED ×{r.n_cohort_conv}
        </Badge>
      )}
      {r.value_trap_flag && (
        <Badge variant="outline" className="ml-1 text-[10px] text-rose-400 border-rose-600/50">
          CROWD ×{r.n_crowd_90d}
        </Badge>
      )}
    </td>
    <td className="py-2 px-2 text-xs">{r.fund}</td>
    <td className="py-2 px-2 font-mono text-xs text-muted-foreground">{r.accepted_date}</td>
    <td className="py-2 px-2 text-right font-mono text-rose-300">{pct(r.dd_at_buy, 0)}</td>
    <td className="py-2 px-2 text-right font-mono text-xs">{usd(r.delta_value)}</td>
    <td className={`py-2 px-2 text-right font-mono ${
      (r.excess_since || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
      {pct(r.excess_since)}
    </td>
    <td className="py-2 px-2 text-xs text-muted-foreground">{r.sector || "—"}</td>
  </tr>
);

const SignalTable = ({ rows, testid }) => (
  <table className="w-full text-sm" data-testid={testid}>
    <thead className="border-b border-border text-muted-foreground text-xs">
      <tr>
        <th className="py-2 px-2 text-left">Symbol</th>
        <th className="py-2 px-2 text-left">Tier</th>
        <th className="py-2 px-2 text-left">Signal</th>
        <th className="py-2 px-2 text-left">Fund</th>
        <th className="py-2 px-2 text-left">Disclosed</th>
        <th className="py-2 px-2 text-right">DD at buy</th>
        <th className="py-2 px-2 text-right">Δ value</th>
        <th className="py-2 px-2 text-right">Excess since</th>
        <th className="py-2 px-2 text-left">Sector</th>
      </tr>
    </thead>
    <tbody>{rows.map((r, i) => <SignalRow key={`${r.symbol}-${i}`} r={r} />)}</tbody>
  </table>
);

export default function ReboundRadarPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bookOnly, setBookOnly] = useState(true);
  const [soloOnly, setSoloOnly] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/signals/rebound-radar`, {
      headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setData(await res.json());
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/signals/rebound-radar/refresh`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` } });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || "refresh failed");
      toast.success(`Radar refreshed — ${body.n_active} active signals`);
      await load();
    } catch (e) {
      toast.error(String(e.message || e));
    } finally { setBusy(false); }
  };

  const rows = (data?.rows || []).filter((r) =>
    bookOnly ? r.tier === "A" && r.solo : !soloOnly || r.solo);
  const active = rows.filter((r) => r.active);
  const older = rows.filter((r) => !r.active);

  return (
    <div className="p-6 space-y-6" data-testid="rebound-radar-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Radar className="w-6 h-6 text-amber-300" /> Rebound Radar
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            "Lonely knife" long signal — skilled knife-catcher funds opening new
            positions in names ≥30% below their highs. Tier A (≥50% drawdown +
            loss-maker, refined cohort): +28.0% fwd-63d excess OOS (t=2.8).
            Solo buys carry the edge — convergence dilutes it.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border overflow-hidden text-xs">
            <button onClick={() => setBookOnly(true)} data-testid="radar-book-filter"
                    className={`px-2.5 py-1.5 transition-colors ${
                      bookOnly ? "bg-secondary text-foreground font-semibold"
                               : "text-muted-foreground hover:text-foreground"}`}>
              Tier A book
            </button>
            <button onClick={() => setBookOnly(false)} data-testid="radar-watchlist-filter"
                    className={`px-2.5 py-1.5 transition-colors ${
                      !bookOnly ? "bg-secondary text-foreground font-semibold"
                                : "text-muted-foreground hover:text-foreground"}`}>
              Full watchlist
            </button>
          </div>
          {!bookOnly && (
            <Button variant={soloOnly ? "default" : "outline"} size="sm"
                    onClick={() => setSoloOnly(!soloOnly)} data-testid="radar-solo-filter">
              Solo only
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={refresh} disabled={busy}
                  data-testid="radar-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${busy ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {!data ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground">
          Not computed yet — hit Refresh to build the radar.
        </CardContent></Card>
      ) : (
        <>
          <div className="text-xs text-muted-foreground" data-testid="radar-meta">
            {data.n_signals} signals · {data.n_active} in active window (≤90d) ·
            computed {data.computed_at?.slice(0, 16).replace("T", " ")} UTC
          </div>
          <SignalBacktestPanel slug="rebound-radar" lineColor="#fbbf24"
                               accent="text-amber-300" />
          <Card data-testid="radar-active-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                Active signals <span className="text-muted-foreground font-normal">
                (disclosed in last 90 days — inside the measured edge window)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {active.length ? <SignalTable rows={active} testid="radar-active-table" />
                : <p className="text-sm text-muted-foreground py-4">No active signals.</p>}
            </CardContent>
          </Card>
          <Card data-testid="radar-history-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                Recent history <span className="text-muted-foreground font-normal">
                (window expired — outcome tracking)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {older.length ? <SignalTable rows={older} testid="radar-history-table" />
                : <p className="text-sm text-muted-foreground py-4">—</p>}
            </CardContent>
          </Card>
          <Card data-testid="radar-cohort-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                Knife-catcher cohort <span className="text-muted-foreground font-normal">
                (graded pre-Jul-2024, skill persists OOS)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-muted-foreground text-xs">
                  <tr>
                    <th className="py-2 px-2 text-left">Fund</th>
                    <th className="py-2 px-2 text-left">Tier</th>
                    <th className="py-2 px-2 text-right"># knives (train)</th>
                    <th className="py-2 px-2 text-right">EB grade</th>
                    <th className="py-2 px-2 text-right">Avg fwd-63d excess</th>
                    <th className="py-2 px-2 text-right">Hit rate</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.cohort || []).map((f) => (
                    <tr key={f.cik} className="border-b border-border/40"
                        data-testid={`radar-cohort-${f.cik}`}>
                      <td className="py-1.5 px-2">{f.name}</td>
                      <td className="py-1.5 px-2">
                        {f.tiers.includes("refined") ? (
                          <Badge variant="outline"
                                 className="text-[10px] bg-amber-500/15 text-amber-300 border-amber-500/50">
                            REFINED
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] text-muted-foreground">base</Badge>
                        )}
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono">{f.n_knives}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-sky-300">
                        {f.eb_grade != null ? pct(f.eb_grade) : "—"}
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono text-emerald-300">
                        {pct(f.avg_fwd63_excess)}
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono">{pct(f.hit_rate, 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
